import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, AlertCircle, ChevronDown, Clock3, CheckCircle2, Banknote } from 'lucide-react';
import { affiliateDashboardAPI } from '../../utils/api';
import { useAffiliateAuth } from '../../contexts/AffiliateAuthContext';
import SaudiRiyalIcon from './SaudiRiyalIcon';
import AffiliateCurrencyAmount from './AffiliateCurrencyAmount';

const AffiliateDashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { affiliate } = useAffiliateAuth();
  const FIXED_COMMISSION_AMOUNT = 300;
  const getCurrentMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthFilter, setMonthFilter] = useState(getCurrentMonthKey());
  const [monthOptions, setMonthOptions] = useState(['all']);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const monthDropdownRef = useRef(null);

  useEffect(() => {
    loadDashboardData(getCurrentMonthKey());
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
        setIsMonthDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadDashboardData = async (selectedMonth = monthFilter || getCurrentMonthKey()) => {
    try {
      setIsLoading(true);
      setError(null);

      const [statsData, referralsData] = await Promise.all([
        affiliateDashboardAPI.getStats(selectedMonth),
        affiliateDashboardAPI.getReferrals(selectedMonth),
      ]);

      setStats(statsData);
      setReferrals(referralsData.referrals || []);
      const apiMonthOptions = Array.isArray(statsData.monthOptions) ? statsData.monthOptions.filter(Boolean) : [];
      setMonthOptions(apiMonthOptions.includes('all') ? apiMonthOptions : ['all', ...apiMonthOptions]);
      setMonthFilter(statsData.monthFilter || selectedMonth || 'all');
    } catch (err) {
      console.error('Error loading affiliate dashboard:', err);
      setError(t('affiliateDashboard.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatMonthFilterLabel = (monthKey) => {
    const allLabel = t('common.all');
    if (!monthKey || monthKey === 'all') return allLabel === 'common.all' ? 'All' : allLabel;
    const [yearStr, monthStr] = String(monthKey).split('-');
    const year = Number.parseInt(yearStr, 10);
    const monthIndex = Number.parseInt(monthStr, 10) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return monthKey;
    }
    const date = new Date(year, monthIndex, 1);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na');
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20', label: t('affiliateDashboard.status.pending') },
      confirmed: { color: 'bg-green-500/10 text-green-600 border-green-500/20', label: t('affiliateDashboard.status.confirmed') },
      paid: { color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', label: t('affiliateDashboard.status.paid') },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>{config.label}</span>;
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('affiliateDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen p-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className={`flex ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <div className={i18n.language === 'ar' ? 'mr-3' : 'ml-3'}>
              <h3 className="text-sm font-medium text-destructive">{t('common.error')}</h3>
              <p className="mt-2 text-sm text-destructive/80">{error}</p>
              <button onClick={() => loadDashboardData()} className="mt-4 text-sm font-medium text-destructive hover:text-destructive/80 underline">
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background text-foreground">
      <div className={`${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        <h1 className="text-3xl font-bold text-foreground">{t('affiliateDashboard.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('affiliateDashboard.welcome', { username: affiliate?.username || t('affiliateDashboard.affiliateFallback') })}</p>
        <div className={`mt-4 flex items-center gap-2 ${i18n.language === 'ar' ? 'justify-end' : 'justify-start'}`}>
          <div className="relative" ref={monthDropdownRef}>
            <button
              id="affiliate-month-filter"
              type="button"
              onClick={() => setIsMonthDropdownOpen((prev) => !prev)}
              className="min-w-[180px] px-3 py-2 rounded-lg border border-border bg-card text-card-foreground text-sm flex items-center justify-between gap-2"
            >
              <span>{formatMonthFilterLabel(monthFilter || 'all')}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isMonthDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMonthDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {(monthOptions || ['all']).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setIsMonthDropdownOpen(false);
                      loadDashboardData(option);
                    }}
                    className={`w-full px-3 py-2 text-sm text-left hover:bg-muted ${(monthFilter || 'all') === option ? 'bg-muted font-medium' : 'text-card-foreground'}`}
                  >
                    {formatMonthFilterLabel(option)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className={`text-lg font-semibold text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalReferrals')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-blue-500/10 rounded-lg"><Users className="w-6 h-6 text-blue-500" /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalReferrals')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{stats?.totalReferrals || 0}</p></div></div></div>
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-yellow-500/10 rounded-lg"><Clock3 className="w-6 h-6 text-yellow-500" /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.status.pending')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{stats?.pendingReferrals || 0}</p></div></div></div>
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-green-500/10 rounded-lg"><CheckCircle2 className="w-6 h-6 text-green-500" /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.status.confirmed')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{stats?.confirmedReferrals || 0}</p></div></div></div>
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-blue-500/10 rounded-lg"><Banknote className="w-6 h-6 text-blue-500" /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.status.paid')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{stats?.paidReferrals || 0}</p></div></div></div>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className={`text-lg font-semibold text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalEarnings')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-yellow-500/10 rounded-lg"><SaudiRiyalIcon /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalPendingEarnings')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}><AffiliateCurrencyAmount amount={stats?.totalPendingEarnings || 0} /></p></div></div></div>
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-green-500/10 rounded-lg"><SaudiRiyalIcon /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalConfirmedEarnings')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}><AffiliateCurrencyAmount amount={stats?.totalConfirmedEarnings || 0} /></p></div></div></div>
          <div className="bg-card border border-border p-6 rounded-xl shadow-sm"><div className={`flex items-center ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}><div className="p-2 bg-blue-500/10 rounded-lg"><SaudiRiyalIcon /></div><div className={i18n.language === 'ar' ? 'mr-4' : 'ml-4'}><p className={`text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.totalPaidEarnings')}</p><p className={`text-2xl font-bold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}><AffiliateCurrencyAmount amount={stats?.totalPaidEarnings || 0} /></p></div></div></div>
        </div>
      </div>

      <div className="bg-card border border-border p-6 rounded-lg shadow">
        <div className={`flex items-center justify-between mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <h2 className={`text-xl font-semibold text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.recentReferrals')}</h2>
        </div>
        {referrals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.user')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.statusLabel')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.commission')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.referralDate')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.confirmedDate')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('affiliateDashboard.paidDate')}</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((referral) => (
                  <tr key={referral.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4"><div><p className="font-medium text-card-foreground">{referral.username || t('common.na')}</p><p className="text-sm text-muted-foreground">{referral.email || t('common.na')}</p></div></td>
                    <td className="py-3 px-4">{getStatusBadge(referral.status)}</td>
                    <td className="py-3 px-4"><span className="font-medium text-card-foreground">
                      <AffiliateCurrencyAmount
                        amount={
                          referral.status === 'pending' &&
                          (!referral.commission_amount || Number(referral.commission_amount) === 0)
                            ? FIXED_COMMISSION_AMOUNT
                            : (referral.commission_amount || 0)
                        }
                      />
                    </span></td>
                    <td className="py-3 px-4"><span className="text-sm text-muted-foreground">{formatDate(referral.created_at)}</span></td>
                    <td className="py-3 px-4"><span className="text-sm text-muted-foreground">{formatDate(referral.conversion_date)}</span></td>
                    <td className="py-3 px-4"><span className="text-sm text-muted-foreground">{formatDate(referral.paid_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t('affiliateDashboard.noReferralsYet')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;

